import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter, Link } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { WarningModal } from "../../components/Modal";
import Icon from "../../components/Icon";
import {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
} from "./redux/actions";
import { SEND_STATUS_LABEL } from "./api";
import "./style.scss";

const STATUS_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "SENT", label: "Sent" },
  { value: "DRAFT", label: "Draft" },
  { value: "FAILED", label: "Failed" }
];

const PAGE_SIZE = 20;

const CONTENT_TYPE_LABEL = {
  GENERAL: "General",
  PROMO: "Promo",
  URGENT: "Urgent"
};

const CONFIRM_TITLE = {
  SEND: notification =>
    `Send "${notification.header}" to the ${notification.userScope} customer group now?`,
  DELETE: notification => `Delete "${notification.header}"?`,
  // A delivered campaign is hidden rather than deleted: the customers who already have it
  // keep it out of sight, and its Reach / View history stays.
  DEACTIVATE: notification =>
    `Hide "${notification.header}" from customers' inbox? (Reach / View totals are kept)`,
  ACTIVATE: notification => `Show "${notification.header}" in the app again?`,
  // A scheduled send that has not gone out yet: cancelling keeps the record but stops the send.
  CANCEL: notification =>
    `Cancel the scheduled send of "${notification.header}"? It stays in the list as cancelled and will not be sent.`,
  RESUME: notification =>
    `Resume the scheduled send of "${notification.header}"? It sends at its scheduled time (or now if that time has passed).`
};

const formatDate = value => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? "-" : date.toLocaleString("th-TH");
};

/*
 * A campaign that has gone out can no longer be edited or deleted; it is history.
 *
 * The test is the send time, not the send status: a campaign whose pushes all failed
 * still reached every recipient's inbox and may already have been read. This mirrors
 * the backend guard, which refuses the edit either way.
 */
const isDelivered = notification => Boolean(notification.timeSent);

/*
 * A campaign that is queued to go out but has not been sent yet: scheduled (a Send date in
 * the future) or otherwise pending. These are the ones an operator can still cancel.
 */
const isScheduledPending = notification =>
  !isDelivered(notification) && notification.sendStatus === "PENDING";

/*
 * A scheduled send the operator has cancelled. Cancelling flips the campaign to INACTIVE,
 * which the scheduler never picks up (it only dispatches ACTIVE + PENDING rows), so the
 * record and its Send date stay while the send is stopped. Resuming turns it back to ACTIVE.
 */
const isCancelledSchedule = notification =>
  isScheduledPending(notification) && notification.status !== "ACTIVE";

function NotificationList(props) {
  const {
    REQNotificationList,
    SENDxNotification,
    DELxNotification,
    SETxNotificationStatus,
    CleanUpNotificationList,
    REQStrNotificationList,
    NotificationList,
    Pagination,
    errorMessage
  } = props;


  const [oSearch, SETxSearch] = useState("");
  const [oSubmittedSearch, SETxSubmittedSearch] = useState("");
  const [oFilter, SETxFilter] = useState("ALL");
  const [oPage, SETxPage] = useState(1);
  const [oConfirm, SETxConfirm] = useState(null);

  const filters = {
    search: oSubmittedSearch,
    sendStatus: oFilter,
    page: oPage,
    limit: PAGE_SIZE
  };

  useEffect(() => {
    REQNotificationList(filters);
    return () => {
      CleanUpNotificationList();
    };
  }, [oSubmittedSearch, oFilter, oPage]);

  const onSearch = event => {
    event.preventDefault();
    SETxPage(1);
    SETxSubmittedSearch(oSearch.trim());
  };

  const onConfirm = () => {
    if (!oConfirm) {
      return;
    }
    if (oConfirm.action === "SEND") {
      SENDxNotification({ id: oConfirm.notification.id, filters });
    }
    if (oConfirm.action === "DELETE") {
      DELxNotification({ id: oConfirm.notification.id, filters });
    }
    if (oConfirm.action === "DEACTIVATE") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "INACTIVE", filters });
    }
    if (oConfirm.action === "ACTIVATE") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "ACTIVE", filters });
    }
    // Cancel / resume a scheduled send: the same status flip, kept apart so the confirm copy fits.
    if (oConfirm.action === "CANCEL") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "INACTIVE", filters });
    }
    if (oConfirm.action === "RESUME") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "ACTIVE", filters });
    }
    SETxConfirm(null);
  };

  const totalPages = (Pagination && Pagination.totalPages) || 0;

  return (
    <Fragment>
      {REQStrNotificationList ? (
        <div className="funds-list-progress">
          <CircularProgress />
          <p className="funds-list-progress-text">Loading notifications...</p>
        </div>
      ) : null}

      <div className="noti-list-container">
        <div className="noti-list-topbar">
          <div className="noti-list-titles">
            <h2 className="noti-list-heading">Notification List</h2>
            <p className="noti-list-subheading">
              Notifications sent to customers in the Principal TH app — create, edit, and track results
            </p>
          </div>

          <div className="noti-list-actions">
            {/* One box with the icon inside, like the search on the other list pages */}
            <form className="noti-list-search" onSubmit={onSearch}>
              <button type="submit" aria-label="Search">
                <Icon name="search" size={20} />
              </button>
              <input
                type="text"
                placeholder="Search by name, title or code"
                value={oSearch}
                onChange={event => SETxSearch(event.target.value)}
              />
            </form>
            <Link className="noti-list-create" to="/PushNoti_Compose">
              <Icon name="plus" size={18} />
              Create new
            </Link>
          </div>
        </div>

        {errorMessage ? <div className="noti-list-error">{errorMessage}</div> : null}

        <div className="noti-list-filters">
          {STATUS_FILTERS.map(filter => (
            <button
              type="button"
              key={filter.value}
              className={oFilter === filter.value ? "noti-list-filter active" : "noti-list-filter"}
              onClick={() => {
                SETxPage(1);
                SETxFilter(filter.value);
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="noti-list-rows">
          {/* Column headings, like the tables on the other pages; hidden once the rows wrap */}
          {(NotificationList || []).length > 0 ? (
            <div className="noti-list-row noti-list-header" role="row">
              <div className="noti-list-code">Code</div>
              <div className="noti-list-type">Type</div>
              <div className="noti-list-content">Notification</div>
              <div className="noti-list-cell time">Time sent</div>
              <div className="noti-list-cell count">Reach</div>
              <div className="noti-list-cell count">View</div>
              <div className="noti-list-cell count">Click</div>
              <div className="noti-list-status">Status</div>
              <div className="noti-list-row-actions">Actions</div>
            </div>
          ) : null}
          {(NotificationList || []).map(notification => (
            <div className="noti-list-row" key={notification.id}>
              <div className="noti-list-code" title={notification.code}>
                {notification.code}
              </div>

              <span className={`noti-list-type ${(notification.contentType || "").toLowerCase()}`}>
                {CONTENT_TYPE_LABEL[notification.contentType] || notification.contentType}
              </span>

              <div className="noti-list-content">
                <p className="noti-list-title">{notification.header}</p>
                <p className="noti-list-details">{notification.content}</p>
                <p className="noti-list-meta">
                  {notification.name} · {notification.notificationType} ·{" "}
                  {notification.userScope}
                </p>
              </div>

              <div className="noti-list-cell time">
                <span className="noti-list-cell-label">Time sent</span>
                {formatDate(notification.timeSent || notification.scheduledAt)}
              </div>

              <div className="noti-list-cell count">
                <span className="noti-list-cell-label">Reach</span>
                {notification.reach}
              </div>

              <div className="noti-list-cell count">
                <span className="noti-list-cell-label">View</span>
                {notification.view}
              </div>

              <div className="noti-list-cell count">
                <span className="noti-list-cell-label">Click</span>
                {notification.click != null ? notification.click : "-"}
              </div>

              {/* Once sent, the status opens the delivery log: who it reached and who it did not */}
              {isDelivered(notification) ? (
                <Link
                  title="View delivery log"
                  className={`noti-list-status link ${notification.sendStatus.toLowerCase()}`}
                  to={`/Notification_DeliveryLog?id=${notification.id}`}
                >
                  {SEND_STATUS_LABEL[notification.sendStatus] || notification.sendStatus}
                  <Icon name="arrowRight" size={14} />
                </Link>
              ) : (
                <div
                  className={`noti-list-status ${
                    isCancelledSchedule(notification) ? "cancelled" : notification.sendStatus.toLowerCase()
                  }`}
                >
                  {isCancelledSchedule(notification)
                    ? SEND_STATUS_LABEL.CANCELLED
                    : SEND_STATUS_LABEL[notification.sendStatus] || notification.sendStatus}
                </div>
              )}

              <div className="noti-list-row-actions">
                {isDelivered(notification) ? (
                  <Fragment>
                    <Link className="noti-list-edit ghost" to={`/PushNoti_Compose?id=${notification.id}`}>
                      <Icon name="eye" />
                      View
                    </Link>
                    {/* Sent already: it cannot be edited or deleted, only hidden from the app */}
                    <button
                      type="button"
                      className={notification.status === "ACTIVE" ? "noti-list-delete" : "noti-list-send"}
                      onClick={() =>
                        SETxConfirm({
                          action: notification.status === "ACTIVE" ? "DEACTIVATE" : "ACTIVATE",
                          notification
                        })
                      }>
                      <Icon name={notification.status === "ACTIVE" ? "eyeOff" : "eye"} />
                      {notification.status === "ACTIVE" ? "Hide" : "Show"}
                    </button>
                  </Fragment>
                ) : (
                  <Fragment>
                    <Link className="noti-list-edit" to={`/PushNoti_Compose?id=${notification.id}`}>
                      <Icon name="edit" />
                      Edit
                    </Link>
                    {isCancelledSchedule(notification) ? (
                      // A cancelled schedule: let the operator put it back on the scheduler.
                      <button
                        type="button"
                        className="noti-list-send"
                        onClick={() => SETxConfirm({ action: "RESUME", notification })}
                      >
                        <Icon name="refresh" />
                        Resume
                      </button>
                    ) : isScheduledPending(notification) ? (
                      // A live scheduled send: cancel stops it without deleting the record.
                      <button
                        type="button"
                        className="noti-list-cancel"
                        onClick={() => SETxConfirm({ action: "CANCEL", notification })}
                      >
                        <Icon name="close" />
                        Cancel
                      </button>
                    ) : notification.status === "ACTIVE" ? (
                      <button
                        type="button"
                        className="noti-list-send"
                        onClick={() => SETxConfirm({ action: "SEND", notification })}
                      >
                        <Icon name="send" />
                        Send now
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="noti-list-delete"
                      onClick={() => SETxConfirm({ action: "DELETE", notification })}
                    >
                      <Icon name="trash" />
                      Delete
                    </button>
                  </Fragment>
                )}
              </div>
            </div>
          ))}

          {!REQStrNotificationList && (NotificationList || []).length === 0 ? (
            <div className="noti-list-empty">No notifications yet</div>
          ) : null}
        </div>

        {totalPages > 1 ? (
          <div className="noti-list-pager">
            <button type="button" disabled={oPage <= 1} onClick={() => SETxPage(oPage - 1)}>
              Previous
            </button>
            <span>
              Page {Pagination.page} / {totalPages} ({Pagination.total} total)
            </span>
            <button type="button" disabled={oPage >= totalPages} onClick={() => SETxPage(oPage + 1)}>
              Next
            </button>
          </div>
        ) : null}
      </div>

      <WarningModal
        show={oConfirm !== null}
        onHide={() => SETxConfirm(null)}
        onHandleGo={onConfirm}
        onHandleCancel={() => SETxConfirm(null)}
        type={"Disapprove"}
        status={"1"}
        title={oConfirm ? CONFIRM_TITLE[oConfirm.action](oConfirm.notification) : ""}
      />
    </Fragment>
  );
}

const mapStateToProps = state => ({
  REQStrNotificationList: state.notificationListReducer.REQStrNotificationList,
  NotificationList: state.notificationListReducer.NotificationList,
  Pagination: state.notificationListReducer.Pagination,
  errorMessage: state.notificationListReducer.errorMessage
});

const mapDispatchToProps = {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(NotificationList);
